import { escapeSql } from '@rdrive/helper-sql';
import { defineHierarchyChart } from '@rdrive/helper-chart-config';

// Issue breakdown by issue category -> issue type -> issue item -> responsible party -> status
// -> single status, for @rdrive/plugin-chart-stacked-bar's StackedHorizontalBarChart
// (IA_ABSORPTION_PLAN Phase 4 step 12a; was components/charts/CategoryStackedBarChart.vue). Since
// Phase 5 step 5.1 a level table on `defineHierarchyChart` (@rdrive/helper-chart-config) — the
// generated queries equal the old hand-written ones (tooling/parity/hierarchy-chart/). Rendered on
// all three pages under one `chartName` ('categoryStackedBarChart'): pages are never mounted
// together, so the filter registrations cannot collide.
//
// Every level reads `filteredIssues` — the providers ignore the base's `filtered` flag, as they
// always did. The pages bind `skipEmptyLevels`: a project that never fills `BI-Issue-Category`
// opens at the first level that has data, and a drill click into a level nobody fills (e.g. no
// `BI-Issue-Item`) shows the next level down that has data.
//
// The filters read the raw `issues` table and build the drill-down so far: a level's condition
// takes the clicked row's id / parentId, or a value selected at a shallower level
// (`{ drill: '<level>' }`). `filter.skipEmpty` leaves out a condition whose value is empty/null,
// which keeps drill-down working when a project doesn't populate a parent field (e.g. the
// category): the empty level is omitted from the filter instead of filtering everything out.

const STATUS_JOIN = 'JOIN status_templates s ON i.IssueStatusId = s.id';
const present = (column) => `${column} IS NOT NULL AND ${column} != ''`;

export function useIssueCategoryChart({ chartName, filterContext, conn }) {
  return defineHierarchyChart({
    chartName,
    filterContext,
    conn,
    source: { table: 'filteredIssues', alias: 'i' },
    status: { join: STATUS_JOIN },
    filter: { table: 'issues', keys: 'i.Id', skipEmpty: true },
    defaults: { value: 'window' },
    levels: [
      {
        level: 'category', titleKey: 'issueCategory',
        id: 'i.issueCategory', title: 'i.issueCategory', where: present('i.issueCategory'),
        filter: { where: [['i."BI-Issue-Category"', 'id']] },
      },
      {
        level: 'subcategory', titleKey: 'issueType',
        id: 'i.issueType', parentId: 'i.issueCategory', title: 'i.issueType', where: present('i.issueType'),
        groupBy: 'i.issueType, i.issueCategory, s.title, s.color, s.sort',
        orderBy: 'i.issueType, s.sort, s.title ASC',
        filter: { where: [['i."BI-Issue-Category"', 'parentId'], ['i."BI-Issue-Type"', 'id']] },
      },
      {
        level: 'profile', titleKey: 'issueItem',
        id: 'i.issueItem', parentId: 'i.issueType', type: "'Issue'", title: 'i.issueItem', where: present('i.issueItem'),
        groupBy: 'i.issueItem, i.issueType, s.title, s.color, s.sort',
        orderBy: 'i.issueItem, s.sort, s.title ASC',
        filter: { where: [['i."BI-Issue-Item"', 'id'], ['i."BI-Issue-Type"', 'parentId']] },
      },
      {
        level: 'subitem', titleKey: 'responsibleParty',
        id: 'i.responsibleParty', parentId: 'i.issueItem', type: "'Issue'", title: 'i.responsibleParty',
        where: present('i.responsibleParty'),
        groupBy: 'i.responsibleParty, i.issueItem, s.title, s.color, s.sort',
        orderBy: 'i.responsibleParty, s.sort, s.title ASC',
        filter: {
          where: [
            ['i."BI-Responsible-Party"', 'id'],
            ['i."BI-Issue-Item"', { drill: 'profile' }],
            ['i."BI-Issue-Type"', { drill: 'subcategory' }],
          ],
        },
      },
      {
        level: 'status', titleKey: 'status',
        id: 's.title', parentId: 'i.responsibleParty', type: "'Issue'", title: 's.title',
        where: present('i.responsibleParty'),
        groupBy: 's.title, i.responsibleParty, s.color, s.sort',
        orderBy: 's.sort, s.title ASC',
        // The status condition is always written, even for an empty title (a function condition
        // is never skipped); the drill-down conditions after it follow skipEmpty
        filter: {
          joins: ['JOIN status_templates st ON i.IssueStatusId = st.id'],
          where: [
            ({ id }) => `st.title = '${escapeSql(id)}'`,
            ['i."BI-Responsible-Party"', 'parentId'],
            ['i."BI-Issue-Item"', { drill: 'profile' }],
            ['i."BI-Issue-Type"', { drill: 'subcategory' }],
          ],
        },
      },
      // The terminal level has no filter, so a click there stays inert
      {
        level: 'singlestatus', titleKey: 'singlestatus',
        id: 's.title', title: 's.title',
        groupBy: 's.id, s.title, s.color', having: 'COUNT(*) > 0', orderBy: 's.title',
      },
    ],
  });
}
